import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import type {
  VendorSignupRequest,
  VendorSignupResponse,
} from 'src/api/ProactApiClient';

import {vendorSignupApiClient} from 'src/api/ProactApiClientInstances';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';

export interface VendorState {
  isError: boolean;
  isSuccess: boolean;
  isLoading: boolean;
  isSignupCodeValidated: boolean;
  isSignupCodeValid: boolean;
  isSignupAttempted: boolean;
  isSignupCompleted: boolean;
  signedupUser?: VendorSignupResponse;
  message: string;
}

export const checkVendorCode = createAsyncThunk(
  'vendor/checkVendorCode',
  async (code: string, thunkAPI) => {
    try {
      return await vendorSignupApiClient.isSignupCodeValid(code);
    } catch (error) {
      return thunkAPI.rejectWithValue(parseErrorMessage(error));
    }
  }
);

export const signupVendorUser = createAsyncThunk(
  'vendor/signupVendorUser',
  async (request: VendorSignupRequest, thunkAPI) => {
    try {
      return await vendorSignupApiClient.signupUser(request);
    } catch (error) {
      return thunkAPI.rejectWithValue(parseErrorMessage(error));
    }
  }
);

// Initial state
const initialState: VendorState = {
  isError: false,
  isSuccess: false,
  isLoading: false,
  isSignupCodeValidated: false,
  isSignupCodeValid: false,
  isSignupAttempted: false,
  isSignupCompleted: false,
  signedupUser: undefined,
  message: '',
};

// Slice
export const vendorSlice = createSlice({
  name: 'vendor',
  initialState,
  reducers: {
    resetVendorState: () => initialState,
    resetVendorErrors: state => {
      state.message = '';
      state.isError = false;
      state.isLoading = false;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(checkVendorCode.pending, state => {
        state.isLoading = true;
        state.isSuccess = false;
        state.isError = false;
        state.isSignupCodeValid = false;
        state.isSignupCodeValidated = false;
        state.message = '';
      })
      .addCase(checkVendorCode.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.isSignupCodeValidated = true;
        state.isSignupCodeValid = action.payload;
      })
      .addCase(checkVendorCode.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(signupVendorUser.pending, state => {
        state.isLoading = true;
        state.isSuccess = false;
        state.isError = false;
        state.isSignupAttempted = true;
        state.isSignupCompleted = false;
        state.signedupUser = undefined;
        state.message = '';
      })
      .addCase(signupVendorUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.isSignupAttempted = true;
        state.isSignupCompleted = true;
        state.signedupUser = action.payload;
      })
      .addCase(signupVendorUser.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      });
  },
});

export const {resetVendorState, resetVendorErrors} = vendorSlice.actions;

export default vendorSlice.reducer;
